/* ============================================================
   KAN-133 — Searchanise search integration
   ============================================================
   Opt-in via theme setting `searchanise_enabled` (config/settings_schema.json).
   Off by default — none of this runs unless a merchant turns it on for a
   given theme (dev/staging), so it ships with zero risk to the live theme.

   Calls the Searchanise Search API directly (docs.searchanise.io/search-api)
   instead of loading Searchanise's own widget script, so results render into
   the theme's own markup/CSS (predictive-search__*, product-card, etc.)
   rather than a bolted-on third-party UI — see the KAN-133 Jira ticket for
   the full architecture writeup.

   Loaded from layout/theme.liquid, gated on that setting — a standalone
   asset rather than part of skb-custom.js, so the feature ships, caches and
   reverts as one unit.

   Scope: the header autocomplete (products, suggestions, collections, blog
   posts and pages) and the /search page (products, facets, sorting,
   pagination). Merchandising (boosting, pinning, redirects) stays where it
   belongs, in the Searchanise dashboard.

   Two custom elements, each a drop-in replacement for a native one:
     - <searchanise-predictive-search> replaces <predictive-search> in the
       header (snippets/header-search.liquid). theme.js's <predictive-search>
       is a compiled bundle we can't edit or repoint, so this is a parallel
       implementation of the same contract (shadow "results" slot, same
       aria-owns form wiring) calling Searchanise's /getwidgets endpoint. It
       also carries the matching collections, blog posts and pages.
     - <searchanise-search-results> drives the whole /search page
       (sections/main-search.liquid) from /getresults: product grid with
       numbered pagination, the facet sidebar/drawer, the sort popover and
       the active-filter chips. Filter, sort and page state lives in the URL
       (filter.<name>=…, sort_by=…, page=…) so results stay shareable and the
       back button works.

   The native Shopify path further down main-search.liquid is untouched and
   remains the no-JS / API-outage fallback.
============================================================ */
(function () {
  if (window.customElements.get('searchanise-predictive-search')) return;

  var SEARCHANISE_ENDPOINT = 'https://searchserverapi1.com/';

  // How many products the header dropdown shows.
  var SEARCHANISE_WIDGET_ITEMS = 6;

  // Compares two request URLs by what they actually ask for rather than by
  // string, so the inline prefetch in main-search.liquid does not have to
  // reproduce this function's parameter order to be picked up.
  function normalizeRequestUrl(url) {
    var parts = String(url).split('?');
    var entries = [];

    new URLSearchParams(parts[1] || '').forEach(function (value, key) {
      entries.push(key + '=' + value);
    });

    entries.sort();

    return parts[0] + '?' + entries.join('&');
  }

  function searchaniseFetch(action, params) {
    var apiKey = (window.themeVariables && window.themeVariables.settings.searchaniseApiKey) || '';
    var query = ['apiKey=' + encodeURIComponent(apiKey)];

    Object.keys(params || {}).forEach(function (key) {
      query.push(encodeURIComponent(key) + '=' + encodeURIComponent(params[key]));
    });

    var url = SEARCHANISE_ENDPOINT + action + '?' + query.join('&');
    var prefetch = window.__skbSearchanisePrefetch;
    var request;

    // Claimed at most once: a later request that happens to look identical
    // (the shopper cleared every filter again, say) must not be answered from
    // a response that is by then stale.
    if (prefetch && !prefetch.claimed && normalizeRequestUrl(prefetch.url) === normalizeRequestUrl(url)) {
      prefetch.claimed = true;
      request = prefetch.promise;
    } else {
      request = fetch(url).then(function (res) {
        return res.json();
      });
    }

    return withTimeout(request).then(function (data) {
      if (data && data.error) throw new Error('Searchanise: ' + data.error);
      return data;
    });
  }

  // The API normally answers in well under a second, but a request that stalls
  // has no natural end: fetch() never rejects on its own, so the results page
  // would sit on aria-busy forever with nothing on screen. Rejecting instead
  // lets the callers fall through to the state they already have for a failed
  // request — the empty state on the results page, a silent warning in the
  // header dropdown. The prefetched promise is raced rather than aborted,
  // since it was started before this code ran.
  var SEARCHANISE_TIMEOUT_MS = 10000;

  function withTimeout(promise) {
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () {
        reject(new Error('Searchanise: request timed out'));
      }, SEARCHANISE_TIMEOUT_MS);

      promise.then(
        function (value) {
          clearTimeout(timer);
          resolve(value);
        },
        function (error) {
          clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  function skbDebounce(fn, delay) {
    var timer;
    return function () {
      var args = arguments;
      var context = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    };
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Adapted from Shopify's standard money.js — Searchanise returns price as a
  // decimal amount (e.g. "800.0000"), not cents, so this takes a decimal float.
  function formatMoney(amount, format) {
    var settings = window.themeVariables.settings;
    format = format || settings.moneyFormat || '${{amount}}';

    function withDelimiters(cents, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;

      var value = (cents / 100).toFixed(precision);
      var parts = value.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';

      return dollars + centsPart;
    }

    var cents = Math.round((parseFloat(amount) || 0) * 100);
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var token = match ? match[1] : 'amount';
    var value;

    switch (token) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', '');
        break;
      default:
        value = withDelimiters(cents, 2);
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function computeSavingsText(price, listPrice, settings) {
    if (settings.discountMode === 'percentage') {
      return Math.round(((listPrice - price) / listPrice) * 100) + '%';
    }
    return formatMoney(listPrice - price);
  }

  // The search API doesn't return the `custom.badges` metafield the theme
  // normally reads (product-badges.liquid), but this store's own tags
  // happen to mirror it 1:1 for these three (confirmed against live data on
  // KAN-133) — "RESTOCKED" is inventory-event-driven, not a static tag, so
  // it can't be derived this way. Matched case-insensitively; `.badge`'s own
  // CSS uppercases the label, so the tag's original casing doesn't matter.
  var CUSTOM_BADGE_TAGS = ['limited', 'new', 'top rated'];

  function extractCustomBadges(item) {
    var tags = (item.tags || '').split('[:ATTR:]');
    var badges = [];

    tags.forEach(function (tag) {
      var trimmed = tag.trim();
      if (CUSTOM_BADGE_TAGS.indexOf(trimmed.toLowerCase()) !== -1) badges.push(trimmed);
    });

    return badges;
  }

  // The currency the storefront is quoting in right now. `window.Shopify.currency`
  // is injected on every storefront page and is the live value — it survives a
  // soft market switch that the Liquid copy was rendered before — so it wins,
  // with the Liquid copy (itself read from the cart, not from the localization
  // country) as the fallback.
  function activeCurrency() {
    var shopifyCurrency = window.Shopify && window.Shopify.currency;

    return (shopifyCurrency && shopifyCurrency.active) || window.themeVariables.settings.activeCurrencyCode;
  }

  // Shopify's live base → presentment rate, for the one case the catalogue
  // cannot answer. It knows nothing about a market's price adjustments or its
  // rounding rule, so it is an approximation — but an approximation in the
  // right currency, which a raw base amount is not.
  function activeCurrencyRate() {
    var shopifyCurrency = window.Shopify && window.Shopify.currency;
    var rate = shopifyCurrency ? parseFloat(shopifyCurrency.rate) : NaN;

    return !isNaN(rate) && rate > 0 ? rate : 1;
  }

  // Searchanise indexes root-relative storefront paths ("/products/…",
  // "/collections/…"), and a few URLs here are built by hand. On a subfolder
  // market (/en-gb, /en-ca, /en-au) the market lives in the URL itself, so an
  // unprefixed link — a product, a suggestion, the quick-add form — quietly
  // drops the shopper back into the default US market and USD. Shopify sets
  // `routes.root` to that subfolder ("/en-gb/"), or "/" everywhere else.
  function localizedPath(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    path = String(path == null ? '' : path);

    if (root === '/' || path.charAt(0) !== '/' || path.charAt(1) === '/') return path;
    if (path === root.slice(0, -1) || path.indexOf(root) === 0) return path;

    return root + path.slice(1);
  }

  // Searchanise returns per-market prices on each variant as
  // price_<COUNTRY>_<CURRENCY> / list_price_<COUNTRY>_<CURRENCY> (e.g.
  // price_IE_EUR, price_GB_GBP) — the top-level item.price/list_price are
  // always in the shop's base currency. Without this, a shopper browsing in
  // a non-base currency sees the right currency symbol (moneyFormat is
  // already localized by Liquid) paired with the wrong (base-currency)
  // amount.
  //
  // The shopper's own country is tried first, then any country sharing their
  // currency. Eurozone countries currently all carry the same value, so the
  // fallback is a no-op today, but it stops being one the moment a market
  // gets its own price adjustment — matching the exact country is the only
  // reading that stays correct either way.
  //
  // No base-currency gate here on purpose: an earlier version skipped the
  // lookup entirely when shop.currency matched the active currency, which
  // silently produced base-currency amounts under a local symbol if that
  // comparison was ever wrong. Looking the market field up unconditionally
  // costs nothing and cannot fail that way.
  function findMarketAmount(variant, fieldPrefix, countryCode, currencyCode) {
    if (!variant || !currencyCode) return null;

    var exact = fieldPrefix + countryCode + '_' + currencyCode;
    if (countryCode && variant[exact] != null) {
      var exactValue = parseFloat(variant[exact]);
      if (!isNaN(exactValue) && exactValue > 0) return exactValue;
    }

    var suffix = '_' + currencyCode;

    for (var key in variant) {
      if (key.indexOf(fieldPrefix) === 0 && key.slice(-suffix.length) === suffix) {
        var value = parseFloat(variant[key]);
        if (!isNaN(value) && value > 0) return value;
      }
    }

    return null;
  }

  // The item shape carries stock two ways: per-variant `available` when the
  // variants are indexed, and a flat `quantity` otherwise.
  function isItemAvailable(item) {
    var variants = item.shopify_variants || [];

    return variants.length
      ? variants.some(function (variant) {
          return variant.available === '1' || variant.available === 1 || variant.available === true;
        })
      : (parseFloat(item.quantity) || 0) > 0;
  }

  // KAN-133 (1a/1b): "Show out of stock products at the end of results list"
  // is a Searchanise dashboard preference, and it is applied server-side — a
  // response already arrives with the sold-out items last, whatever the sort.
  // This is the belt to that braces: a stable partition that is a no-op when
  // the API has already done it, and still puts sold-out products last if the
  // preference is ever turned off (or does not survive a future sort option).
  // Stable, so the relevance order within each group is exactly as returned.
  function sortAvailableFirst(items) {
    var available = [];
    var unavailable = [];

    items.forEach(function (item) {
      (isItemAvailable(item) ? available : unavailable).push(item);
    });

    return available.concat(unavailable);
  }

  // KAN-133 (2a): highlights what the shopper typed inside a suggestion, the
  // way the Searchanise widget does. Tokens are matched case-insensitively
  // and independently, so "tiger eye" lights up both words in "red tiger
  // eye". Returns escaped HTML — the <mark> tags are the only markup that
  // can come out of it.
  function highlightQuery(text, query) {
    var value = String(text == null ? '' : text);
    var tokens = String(query || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(function (token) {
        return token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      });

    if (!tokens.length) return escapeHtml(value);

    var pattern = new RegExp('(' + tokens.join('|') + ')', 'gi');
    var out = '';
    var lastIndex = 0;
    var match;

    while ((match = pattern.exec(value)) !== null) {
      out += escapeHtml(value.slice(lastIndex, match.index)) + '<mark class="searchanise-highlight">' + escapeHtml(match[0]) + '</mark>';
      lastIndex = match.index + match[0].length;
      // A zero-length match would spin forever; nothing sane produces one
      // here, but the guard costs one comparison.
      if (match[0].length === 0) pattern.lastIndex++;
    }

    return out + escapeHtml(value.slice(lastIndex));
  }

  // KAN-133: live market prices from Shopify's Storefront API. A market that
  // converts from the base currency re-prices every day with the exchange
  // rate, while Searchanise only re-reads prices when it syncs, so its
  // price_<COUNTRY>_<CURRENCY> fields drift away from the product page between
  // syncs (1620 vs 1618 DKK on the same product, seen while building this). One
  // batched request per result set, keyed by the same first variant
  // resolveItemPrices reads, asks Shopify for what this market charges today.
  //
  // Empty on purpose: Shopify's tokenless Storefront access covers product
  // prices, and this query costs a few points against its complexity cap. A
  // public token (minted by scripts/storefront-token.mjs, safe to ship in the
  // browser) is only needed if this ever reads something tokenless cannot,
  // such as metafields or inventory.
  var STOREFRONT_ACCESS_TOKEN = '';
  var STOREFRONT_API_VERSION = '2026-07';

  // How long results wait for live prices before rendering without them. An
  // answer that lands later is not thrown away: liveRenderedPrices patches the
  // cards already on screen, so a slow network costs a brief catalogue price,
  // never a wrong one that stays. (A hard 2.5s cut-off used to drop it, and on
  // a cold connection from Asia the request took 2.54s, so staging2 showed
  // Searchanise's €604.95 against €605.95 in the cart.)
  var LIVE_PRICE_WAIT_MS = 1500;

  // Only a request that is genuinely stuck is abandoned.
  var LIVE_PRICE_TIMEOUT_MS = 15000;

  var LIVE_PRICE_QUERY =
    'query SearchPrices($ids: [ID!]!, $country: CountryCode) @inContext(country: $country) {' +
    ' nodes(ids: $ids) { ... on ProductVariant { id price { amount currencyCode } compareAtPrice { amount currencyCode } } } }';

  function storefrontEndpoint() {
    var shop = window.Shopify && window.Shopify.shop;
    return shop ? 'https://' + shop + '/api/' + STOREFRONT_API_VERSION + '/graphql.json' : null;
  }

  // Warm the connection to the Storefront API host as soon as this script runs,
  // so the TLS handshake is already done by the time a shopper has typed a
  // query. `crossorigin` matches the credential-less fetch below — a
  // preconnect in the other connection pool would be wasted.
  (function preconnectStorefront() {
    var endpoint = storefrontEndpoint();
    if (!endpoint) return;

    var link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = endpoint.split('/api/')[0];
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  })();

  // Fetches live prices for `items` and sets `livePrice` on each one Shopify
  // answers for. Resolves as soon as the prices are in, or after
  // LIVE_PRICE_WAIT_MS, whichever is first — the caller renders then. If the
  // answer only lands after that, `onLate(items)` is called so the caller can
  // patch what it already rendered. Never rejects: any failure leaves the items
  // as Searchanise sent them.
  function attachLivePrices(items, onLate) {
    var settings = window.themeVariables.settings;
    var currency = activeCurrency();
    var endpoint = storefrontEndpoint();

    // In the base currency there is no conversion to drift, and the catalogue's
    // own amounts are exact, so the request would only add latency.
    if (!items.length || !endpoint || !currency || currency === settings.shopCurrency) return Promise.resolve(items);

    var byId = {};
    items.forEach(function (item) {
      var variant = (item.shopify_variants || [])[0];
      if (variant && variant.variant_id) byId['gid://shopify/ProductVariant/' + variant.variant_id] = item;
    });

    var ids = Object.keys(byId);
    if (!ids.length) return Promise.resolve(items);

    var controller = new AbortController();
    var timer = setTimeout(function () {
      controller.abort();
    }, LIVE_PRICE_TIMEOUT_MS);

    // GET with no custom headers is a "simple" cross-origin request, so the
    // browser skips the CORS preflight a JSON POST would need — one round trip
    // instead of two. A token header would bring the preflight back, which is
    // one more reason to stay tokenless.
    var url =
      endpoint +
      '?query=' + encodeURIComponent(LIVE_PRICE_QUERY) +
      '&variables=' + encodeURIComponent(JSON.stringify({ ids: ids, country: settings.activeCountryCode }));
    var headers = {};
    if (STOREFRONT_ACCESS_TOKEN) headers['X-Shopify-Storefront-Access-Token'] = STOREFRONT_ACCESS_TOKEN;

    var rendered = false;

    var request = fetch(url, { headers: headers, signal: controller.signal })
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        var found = false;

        ((json && json.data && json.data.nodes) || []).forEach(function (node) {
          var item = node && byId[node.id];
          // A market switch can leave the country and the presentment currency
          // disagreeing for a page (see activeCurrency). An answer in another
          // currency is not this shopper's price, so it is dropped rather than
          // shown under the wrong symbol.
          if (!item || !node.price || node.price.currencyCode !== currency) return;

          item.livePrice = {
            price: parseFloat(node.price.amount) || 0,
            listPrice: node.compareAtPrice ? parseFloat(node.compareAtPrice.amount) || 0 : 0
          };
          found = true;
        });

        if (rendered && found && onLate) onLate(items);
        return items;
      })
      .catch(function (err) {
        if (window.console && console.warn) console.warn('[searchanise] live prices unavailable', err);
        return items;
      })
      .finally(function () {
        clearTimeout(timer);
      });

    var wait = new Promise(function (resolve) {
      setTimeout(resolve, LIVE_PRICE_WAIT_MS);
    });

    return Promise.race([request, wait]).then(function () {
      rendered = true;
      return items;
    });
  }

  // The inside of a card's <price-list>, shared by both card shapes and by the
  // late patch below so the three can never drift apart.
  function priceListInnerHtml(prices, withLabel) {
    var strings = window.themeVariables.strings;

    return (
      '<sale-price class="' + (prices.onSale ? 'text-on-sale' : 'text-subdued') + '">' +
      (withLabel ? '<span class="sr-only">' + escapeHtml(strings.salePrice) + '</span>' : '') +
      escapeHtml(formatMoney(prices.price)) +
      '</sale-price>' +
      (prices.onSale ? '<compare-at-price class="text-subdued line-through">' + escapeHtml(formatMoney(prices.listPrice)) + '</compare-at-price>' : '')
    );
  }

  function priceListAttributes(item, withLabel) {
    var variant = (item.shopify_variants || [])[0];

    return (variant && variant.variant_id ? ' data-variant-id="' + escapeHtml(variant.variant_id) + '"' : '') + (withLabel ? ' data-with-label' : '');
  }

  // Live prices that arrived after `root` was rendered: rewrite each matching
  // card's price in place. Only the price list changes — a discount badge on a
  // card that was already built keeps its catalogue percentage, which on a
  // compare-at product can differ by a fraction of a percent until the next
  // search.
  function liveRenderedPrices(root, items) {
    if (!root) return;

    items.forEach(function (item) {
      var variant = (item.shopify_variants || [])[0];
      if (!item.livePrice || !variant || !variant.variant_id) return;

      var prices = resolveItemPrices(item);

      root.querySelectorAll('price-list[data-variant-id="' + String(variant.variant_id).replace(/"/g, '') + '"]').forEach(function (list) {
        list.innerHTML = priceListInnerHtml(prices, list.hasAttribute('data-with-label'));
      });
    });
  }

  // Card title per Theme settings > Product card > Title; mirrors snippets/card-title.liquid
  // (the card_name metafield isn't in the search index, so "clean" derives the short name from the title).
  function cardTitleHtml(item, settings) {
    var title = item.title || '';
    if (settings.productCardTitleStyle !== 'clean') return escapeHtml(title);
    var lc = ' ' + title.toLowerCase() + ' ';
    var type = String(item.product_type || '').toLowerCase();
    var single = (type.indexOf('bracelet') !== -1 || lc.indexOf(' bracelet ') !== -1) && !/ (set|pack|bundle|pair) /.test(lc);
    if (!single) return escapeHtml(title);
    var name = title.split('(')[0];
    name = (' ' + name + ' ').replace(/ bracelet /gi, ' ').trim();
    var parts = name.split(' - ');
    if (parts.length > 1) name = parts.slice(0, -1).join(', ') + ' & ' + parts[parts.length - 1];
    var size = title.indexOf('(') !== -1 ? title.split('(').pop().split(')')[0] : '';
    if (size.indexOf('mm') === -1) return escapeHtml(name);
    size = size.replace(' mm', 'mm').replace('mm', ' mm').trim();
    var sizeText = String(settings.productCardSizeTemplate || '{{ size }}').replace('{{ size }}', size);
    return '<span class="product-card__name">' + escapeHtml(name) + '</span>' +
      '<span class="product-card__size">' + escapeHtml(sizeText) + '</span>';
  }

  // Builds a simplified <product-card> matching the theme's markup/CSS
  // closely enough to render natively (image aspect ratio setting, price
  // list, sale/sold-out/custom badges, single-variant quick-add) — but
  // without swatches, multi-variant quick-buy, or the RESTOCKED badge,
  // which need full Shopify product objects (options, media, order/
  // inventory history) that the search API's flattened item shape
  // doesn't provide.
  // The prices actually shown, in the shopper's own market currency where the
  // catalogue carries one. Shared by both card shapes so they can never
  // disagree about what a product costs.
  function resolveItemPrices(item) {
    var settings = window.themeVariables.settings;
    var variants = item.shopify_variants || [];
    var country = settings.activeCountryCode;
    var currency = activeCurrency();
    var basePrice = parseFloat(item.price) || 0;
    var baseListPrice = parseFloat(item.list_price) || 0;
    var marketPrice = findMarketAmount(variants[0], 'price_', country, currency);
    var price;
    var listPrice;

    if (item.livePrice) {
      // What Shopify charges in this market right now — see attachLivePrices.
      price = item.livePrice.price;
      listPrice = item.livePrice.listPrice;
    } else if (marketPrice != null) {
      // KAN-133 (item 6): both amounts come from the same side, never one of
      // each. A catalogue that carries `price_DE_EUR` but no matching
      // `list_price_DE_EUR` would otherwise strike a dollar compare-at through
      // a euro price and invent a discount nobody is offering. Carrying the
      // base compare-at over at the rate this product is actually sold at keeps
      // the percentage the badge shows exact.
      var marketListPrice = findMarketAmount(variants[0], 'list_price_', country, currency);

      price = marketPrice;
      listPrice =
        marketListPrice != null
          ? marketListPrice
          : basePrice > 0
            ? baseListPrice * (marketPrice / basePrice)
            : 0;
    } else if (currency && currency !== settings.shopCurrency) {
      // The catalogue has no entry for this currency at all — an unindexed
      // product, or a market added since the last Searchanise sync. Converting
      // at Shopify's live rate lands near the product page rather than on it,
      // but a base-currency number under this market's symbol would not be a
      // price at all.
      var rate = activeCurrencyRate();

      price = basePrice * rate;
      listPrice = baseListPrice * rate;
    } else {
      price = basePrice;
      listPrice = baseListPrice;
    }

    return { price: price, listPrice: listPrice, onSale: listPrice > price };
  }

  // KAN-133 (2c): the compact row the header dropdown shows on phones —
  // thumbnail on the left, name and price on the right. Mirrors the markup
  // the native predictive search emits for the same breakpoint
  // (.horizontal-product-card), so it inherits the theme's styling and reads
  // identically to the live site.
  function buildHorizontalProductCard(item) {
    var prices = resolveItemPrices(item);

    var wrapper = document.createElement('div');
    wrapper.className = 'horizontal-product-card';

    wrapper.innerHTML =
      (item.image_link
        ? '<a href="' + escapeHtml(localizedPath(item.link)) + '" class="horizontal-product-card__figure" data-instant>' +
          '<img class="horizontal-product-card__image" loading="lazy" sizes="100px" alt="' + escapeHtml(item.title) + '" src="' + escapeHtml(item.image_link) + '">' +
          '</a>'
        : '') +
      '<div class="horizontal-product-card__info">' +
      '<div class="v-stack gap-1 justify-items-start">' +
      '<a href="' + escapeHtml(localizedPath(item.link)) + '" class="product-title" data-instant>' + escapeHtml(item.title) + '</a>' +
      '<price-list class="price-list"' + priceListAttributes(item, true) + '>' +
      priceListInnerHtml(prices, true) +
      '</price-list>' +
      '</div>' +
      '</div>';

    return wrapper;
  }

  function buildProductCard(item) {
    var settings = window.themeVariables.settings;
    var strings = window.themeVariables.strings;
    var prices = resolveItemPrices(item);
    var price = prices.price;
    var listPrice = prices.listPrice;
    var onSale = prices.onSale;
    var variants = item.shopify_variants || [];
    var available = isItemAvailable(item);

    var aspectRatio = settings.productImageAspectRatio || 'square';
    var imageClass = 'product-card__image product-card__image--primary aspect-' + aspectRatio.split('_')[0];

    if (aspectRatio.indexOf('crop') !== -1) imageClass += ' object-cover';

    // KAN-133 (2d): the hover image, same as product-card.liquid. The API's
    // `shopify_images` is the product's media list in the same order Liquid
    // sees it, so images[1] is the theme's own `product.media[1]` fallback.
    // The `custom.card_hover_image` metafield the native card prefers is not
    // indexed, so a product that overrides its hover image there shows its
    // second media here instead — the closest match the flat item shape
    // allows, and still correct for every product without that override.
    var images = item.shopify_images || [];
    var secondaryImage = settings.showSecondaryImage && images.length > 1 ? images[1] : null;
    var titleMaxLines = parseInt(settings.productTitleMaxLines, 10) || 0;

    var handle = (item.link || '').replace(/^\/products\//, '').split('?')[0];

    var card = document.createElement('product-card');
    // Same modifiers as product-card.liquid (Theme settings > Product card).
    card.className = 'product-card product-card--wishlist-' + (settings.productCardWishlistPosition || 'top_right') +
      ' product-card--badges-' + (settings.productCardBadgeStyle || 'bar') +
      (settings.productCardTitleArrow === false ? ' product-card--no-arrow' : '');
    var outlinedQuickAdd = settings.productCardQuickAddStyle === 'outlined';
    card.setAttribute('handle', handle);

    var badgesHtml = extractCustomBadges(item)
      .map(function (label) {
        return '<span class="badge badge--custom">' + escapeHtml(label) + '</span>';
      })
      .join('');
    if (!available && settings.showSoldOutBadge) {
      badgesHtml += '<sold-out-badge class="badge badge--sold-out">' + escapeHtml(strings.soldOutBadge) + '</sold-out-badge>';
    }
    if (onSale && settings.showDiscountBadge) {
      var savingsText = computeSavingsText(price, listPrice, settings);
      badgesHtml += '<on-sale-badge class="badge badge--on-sale">' + strings.discountBadgeTemplate.replace('__SAVINGS__', escapeHtml(savingsText)) + '</on-sale-badge>';
    }
    var badgeListHtml = badgesHtml ? '<badge-list class="badge-list badge-list--vertical">' + badgesHtml + '</badge-list>' : '';

    // Single-variant quick-add only — a multi-variant quick-buy needs the full
    // option/variant matrix (native opens a modal for that), which the search
    // API's flattened item shape doesn't provide.
    var quickBuyHtml = '';
    if (settings.showQuickBuy && available && variants.length === 1 && variants[0].variant_id) {
      var strokeWidth = settings.iconStrokeWidth || 1.5;
      quickBuyHtml =
        '<product-form>' +
        '<form method="post" action="' + escapeHtml(localizedPath('/cart/add')) + '" class="shopify-product-form" enctype="multipart/form-data" novalidate data-type="add-to-cart-form">' +
        '<input type="hidden" name="form_type" value="product">' +
        '<input type="hidden" name="on_success" value="force_open_drawer">' +
        '<input type="hidden" name="quantity" value="1">' +
        '<input type="hidden" name="id" value="' + escapeHtml(variants[0].variant_id) + '">' +
        '<button type="submit" class="product-card__quick-add-button' + (outlinedQuickAdd ? ' product-card__quick-add-button--outlined' : '') + '">' +
        '<span class="sr-only">' + escapeHtml(strings.addToCartButton) + '</span>' +
        (outlinedQuickAdd && settings.productCardQuickAddIcon
          ? settings.productCardQuickAddIcon
          : '<svg aria-hidden="true" focusable="false" fill="none" width="12" viewBox="0 0 12 12"><path d="M6 0v12M0 6h12" stroke="currentColor" stroke-width="' + strokeWidth + '"/></svg>') +
        '</button>' +
        '</form>' +
        '</product-form>';
    }

    // Mirrors product-card.liquid: the whole figure (incl. badges/quick-add)
    // is omitted when there's no image, rather than leaving an empty box —
    // .aspect-* sizing lives on the <img> itself, so a figure with no image
    // would collapse to zero height.
    card.innerHTML =
      (item.image_link
        ? '<div class="product-card__figure">' +
          badgeListHtml +
          '<a href="' + escapeHtml(localizedPath(item.link)) + '" class="product-card__media" data-instant>' +
          '<img class="' + imageClass + '" style="width: 100%; height: 100%;" loading="lazy" alt="' + escapeHtml(item.title) + '" src="' + escapeHtml(item.image_link) + '">' +
          (secondaryImage
            ? '<img class="product-card__image product-card__image--secondary" loading="lazy" fetchpriority="low" aria-hidden="true" alt="" src="' +
              escapeHtml(secondaryImage) +
              '">'
            : '') +
          '</a>' +
          (outlinedQuickAdd ? '' : quickBuyHtml) +
          '</div>'
        : '') +
      '<div class="product-card__info">' +
      '<div class="v-stack justify-items-center gap-2">' +
      '<div class="v-stack justify-items-center gap-1">' +
      // KAN-133 (2e): titles are clamped to the same number of lines as the
      // native card (product-card.liquid), so a long name cannot push the
      // price out of line with its neighbours in the grid.
      '<a href="' + escapeHtml(localizedPath(item.link)) + '" class="product-title' + (titleMaxLines > 0 ? ' line-clamp' : '') + '"' +
      (titleMaxLines > 0 ? ' style="--line-clamp-count: ' + titleMaxLines + '"' : '') +
      (settings.productCardTitleStyle === 'clean' ? ' title="' + escapeHtml(item.title) + '"' : '') +
      ' data-instant>' + cardTitleHtml(item, settings) + '</a>' +
      '<price-list class="price-list"' + priceListAttributes(item, false) + '>' +
      priceListInnerHtml(prices, false) +
      '</price-list>' +
      '</div>' +
      '</div>' +
      (outlinedQuickAdd ? quickBuyHtml : '') +
      '</div>';

    return card;
  }

  // === <searchanise-predictive-search> — header autocomplete dropdown ===
  // Mirrors theme.js's native <predictive-search> contract exactly (shadow
  // "results" slot, same aria-owns form wiring), so header-search.liquid can
  // swap between the two with no CSS changes.
  window.customElements.define('searchanise-predictive-search', class extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      this.shadowRoot.appendChild(document.createRange().createContextualFragment('<slot name="results"></slot>'));
      this.abortController = null;
    }

    connectedCallback() {
      this.listenersAbortController = new AbortController();
      this.form = document.querySelector('[aria-owns="' + this.id + '"]');
      if (!this.form) return;

      this.input = this.form.elements.q;
      var signal = this.listenersAbortController.signal;

      this.form.addEventListener('submit', this.onSubmit.bind(this), { signal: signal });
      this.form.addEventListener('reset', this.onCleared.bind(this), { signal: signal });
      this.addEventListener('click', this.onTabClick.bind(this), { signal: signal });
      this.input.addEventListener('input', skbDebounce(this.onInput.bind(this), 280), { signal: signal });
    }

    disconnectedCallback() {
      if (this.listenersAbortController) this.listenersAbortController.abort();
    }

    onSubmit(event) {
      if (this.input.value.trim() === '') event.preventDefault();
    }

    // Plain buttons and panels rather than the theme's <x-tabs>: that element
    // takes its shadow DOM from a declarative <template shadowrootmode>, which
    // the parser only honours in server-rendered HTML — assigning innerHTML,
    // as this dropdown does, would leave it inert.
    onTabClick(event) {
      var button = event.target.closest('[data-searchanise-tab]');
      if (!button || !this.contains(button)) return;

      var key = button.getAttribute('data-searchanise-tab');

      this.querySelectorAll('[data-searchanise-tab]').forEach(function (tab) {
        tab.setAttribute('aria-selected', String(tab === button));
      });

      this.querySelectorAll('[data-searchanise-panel]').forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-searchanise-panel') !== key;
      });
    }

    onCleared() {
      if (this.abortController) this.abortController.abort();
      this.input.focus();
      var slot = this.querySelector('[slot="results"]');
      if (slot) slot.innerHTML = '';
    }

    onInput() {
      var query = this.input.value.trim();
      if (query === '') return this.onCleared();

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      var signal = this.abortController.signal;

      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      searchaniseFetch('getwidgets', {
        q: query,
        items: true,
        suggestions: true,
        // KAN-133 (1b): deliberately over-fetched. Only SEARCHANISE_WIDGET_ITEMS
        // are shown, so asking for twice as many leaves room to drop sold-out
        // products out of the dropdown entirely (rather than merely ordering
        // them last) while still filling every slot — in one request, which
        // matters on a path that runs on every keystroke.
        maxResults: SEARCHANISE_WIDGET_ITEMS * 2,
        suggestionsMaxResults: 4,
        // KAN-133 (2b): collections and pages belong in the dropdown, not on
        // the results page.
        categories: true,
        pages: true,
        categoriesMaxResults: 4,
        pagesMaxResults: 4
      })
        .then(function (data) {
          if (signal.aborted) return;

          // Only the products the dropdown will actually show are priced.
          var shown = ((data && data.items) || []).filter(isItemAvailable).slice(0, SEARCHANISE_WIDGET_ITEMS);

          return attachLivePrices(shown, function (late) {
            if (!signal.aborted) liveRenderedPrices(this.querySelector('[slot="results"]'), late);
          }.bind(this)).then(function () {
            if (signal.aborted) return;
            this.render(data);
          }.bind(this));
        }.bind(this))
        .catch(function (err) {
          if (window.console && console.warn) console.warn('[searchanise]', err);
        })
        .finally(function () {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        });
    }

    render(data) {
      var slot = this.querySelector('[slot="results"]');
      if (!slot) return;

      // KAN-133 (1b): sold-out products are dropped from the dropdown rather
      // than merely pushed down — with only a handful of slots, showing one
      // costs a slot a buyable product could have had. They remain reachable
      // on the results page, which does order rather than hide them.
      var items = ((data && data.items) || []).filter(isItemAvailable).slice(0, SEARCHANISE_WIDGET_ITEMS);
      var suggestions = (data && data.suggestions) || [];
      var query = this.input ? this.input.value.trim() : '';
      var strings = window.themeVariables.strings;

      // KAN-133 (2b): blog posts and pages come back in one `pages` array,
      // told apart by their link, exactly as on the results page.
      var allPages = (data && data.pages) || [];
      var groups = [
        { label: strings.searchCollections, entries: (data && data.categories) || [] },
        {
          label: strings.searchPosts,
          entries: allPages.filter(function (page) {
            return String(page.link).indexOf('/blogs/') === 0;
          })
        },
        {
          label: strings.searchPages,
          entries: allPages.filter(function (page) {
            return String(page.link).indexOf('/blogs/') !== 0;
          })
        }
      ].filter(function (group) {
        return group.entries.length > 0;
      });

      if (items.length === 0 && suggestions.length === 0 && groups.length === 0) {
        slot.innerHTML = '<p class="predictive-search__no-results text-lg">' + escapeHtml(strings.searchNoResults) + '</p>';
        return;
      }

      // Suggestions keep the narrow left-hand column to themselves; products,
      // collections, blog posts and pages share the main column as a tab
      // strip, the way the native predictive search lays them out.
      var hasSuggestions = suggestions.length > 0;

      // Column count is fixed like predictive-search.liquid (4 beside the
      // suggestions, 5 without), not taken from the result count — one or two
      // matches would otherwise stretch to fill the whole dropdown on desktop.
      // A full set of SEARCHANISE_WIDGET_ITEMS still gets a column each.
      var productColumns = Math.max(items.length, hasSuggestions ? 4 : 5);
      var html = '<div class="predictive-search__results' + (hasSuggestions ? ' predictive-search__results--with-suggestions' : '') + '">';

      if (hasSuggestions) {
        html +=
          '<div class="predictive-search__resource-item"><div class="v-stack gap-6 sm:gap-8">' +
          '<p class="predictive-search__category h6 text-subdued">' + escapeHtml(strings.searchSuggestions) + '</p>' +
          '<div class="predictive-search__suggestions scroll-area bleed md:unbleed">';

        suggestions.forEach(function (suggestion) {
          html +=
            '<div><a class="link-reverse" data-instant href="' + escapeHtml(localizedPath('/search')) + '?q=' + encodeURIComponent(suggestion) + '">' +
            highlightQuery(suggestion, query) + '</a></div>';
        });

        html += '</div></div></div>';
      }

      // KAN-133 (2b): Products first, then one tab per resource type that
      // actually matched — a lone Products tab renders as a plain heading
      // (see .searchanise-tabs[data-tab-count="1"] in custom.css), so the
      // strip only appears when there is something to switch to.
      var tabs = [];
      if (items.length > 0) tabs.push({ key: 'products', label: strings.searchProducts });
      groups.forEach(function (group, index) {
        tabs.push({ key: 'group-' + index, label: group.label, entries: group.entries });
      });

      if (tabs.length > 0) {
        html += '<div class="predictive-search__resource-item searchanise-tabs content-tabs" data-tab-count="' + tabs.length + '">';

        html += '<div class="searchanise-tabs__list scroll-area bleed md:unbleed" role="tablist">';
        tabs.forEach(function (tab, index) {
          html +=
            '<button type="button" class="h6" role="tab" data-searchanise-tab="' + tab.key + '"' +
            ' aria-selected="' + (index === 0 ? 'true' : 'false') + '">' +
            escapeHtml(decodeEntities(tab.label)) +
            '</button>';
        });
        html += '</div>';

        tabs.forEach(function (tab, index) {
          html +=
            '<div role="tabpanel" data-searchanise-panel="' + tab.key + '"' + (index === 0 ? '' : ' hidden') + '>';

          if (tab.key === 'products') {
            html +=
              '<div class="v-stack gap-8 sm:gap-12">' +
              '<div class="predictive-search__products" style="--product-list-items-per-row: ' + productColumns + '"></div>' +
              '<div class="justify-self-start sm:justify-self-center">' +
              '<button type="submit" form="predictive-search-form" class="button">' + escapeHtml(strings.searchViewAllResults) + '</button>' +
              '</div></div>';
          } else {
            html +=
              '<div class="searchanise-resource-list v-stack gap-3">' +
              tab.entries
                .map(function (entry) {
                  return (
                    '<div><a class="link-reverse" data-instant href="' + escapeHtml(localizedPath(entry.link)) + '">' +
                    highlightQuery(entry.title, query) +
                    '</a></div>'
                  );
                })
                .join('') +
              '</div>';
          }

          html += '</div>';
        });

        html += '</div>';
      }

      html += '</div>';
      slot.innerHTML = html;

      var productsGrid = slot.querySelector('.predictive-search__products');
      if (productsGrid) {
        // Both shapes are rendered and one is hidden per breakpoint, exactly
        // as the native predictive search does it: the full card on desktop,
        // the compact row on phones (KAN-133, 2c).
        items.forEach(function (item) {
          var desktop = document.createElement('div');
          desktop.className = 'md-max:hidden';
          desktop.appendChild(buildProductCard(item));
          productsGrid.appendChild(desktop);

          var mobile = document.createElement('div');
          mobile.className = 'md:hidden';
          mobile.appendChild(buildHorizontalProductCard(item));
          productsGrid.appendChild(mobile);
        });
      }
    }
  });

  /* ----------------------------------------------------------------------
     Faceting, sorting and the collection/page/blog tabs (KAN-133 phase 2)
     ----------------------------------------------------------------------
     Which facets exist, what they are called and in which order they appear
     is merchant-controlled in the Searchanise dashboard — this code renders
     whatever /getresults hands back, into the theme's own accordion /
     checkbox / <price-range> markup. Nothing here needs changing when a
     filter is added or renamed there.
  ---------------------------------------------------------------------- */

  // Temporary, until the Searchanise dashboard is cleaned up (KAN-133):
  // "Tags" currently exposes 55 raw operational tags ("3) Stone Size_02mm",
  // "bead_shape:rodelle", …) and "Vendor" only ever has one value, so both
  // are noise for shoppers. Deleting these two entries is all that's needed
  // once Kai has turned them off on their side.
  var SEARCHANISE_HIDDEN_FACETS = ['tags', 'vendor'];

  // Shopify's sort_by values (rendered server-side from search.sort_options,
  // so their labels stay translated) mapped onto the Search API's own
  // sortBy/sortOrder pair.
  var SEARCHANISE_SORT_MAP = {
    relevance: { sortBy: 'relevance' },
    'price-ascending': { sortBy: 'price', sortOrder: 'asc' },
    'price-descending': { sortBy: 'price', sortOrder: 'desc' },
    'title-ascending': { sortBy: 'title', sortOrder: 'asc' },
    'title-descending': { sortBy: 'title', sortOrder: 'desc' },
    'created-ascending': { sortBy: 'created', sortOrder: 'asc' },
    'created-descending': { sortBy: 'created', sortOrder: 'desc' },
    'best-selling': { sortBy: 'sales_amount', sortOrder: 'desc' }
  };

  var SEARCHANISE_SLUG_STORAGE_KEY = 'skb:searchanise:facet-slugs';

  // From how many values a filter group gets its own search box (KAN-133, 1d).
  // Mirrors the "If filter values more than" number set in the Searchanise
  // dashboard next to "Show search bar in filters" — their API does not
  // report either setting, so the threshold is kept in step by hand. Change
  // both together if that number is changed there.
  var SEARCHANISE_FACET_SEARCH_MIN = 20;

  // Hides the values of one filter group that do not contain `term`. A value
  // the shopper has already applied always stays visible, so a search cannot
  // hide the checkbox needed to undo it.
  function applyFacetSearch(group, term) {
    var matches = 0;

    group.querySelectorAll('[data-facet-option]').forEach(function (row) {
      var input = row.querySelector('input[type="checkbox"]');
      var keep = !term || (input && input.checked) || row.getAttribute('data-facet-option').indexOf(term) !== -1;

      row.hidden = !keep;
      if (keep) matches++;
    });

    var empty = group.querySelector('[data-facet-search-empty]');
    if (empty) empty.hidden = matches > 0;
  }

  // A bare hex colour, the shape Searchanise sends `color_code` in ("0000FF").
  // Its "Multicolor" family carries "*" instead, which is a marker rather than
  // a colour — it falls through to the theme's own artwork below.
  var SEARCHANISE_HEX_PATTERN = /^[0-9a-f]{3}(?:[0-9a-f]{3})?$/i;

  // The CSS background a swatch should paint, in the same shape the theme's
  // option-value snippet builds it: a flat colour is expressed as a gradient
  // so `--swatch-background` is always a single background value. Returns
  // null when neither Searchanise nor the theme has artwork for the value.
  function swatchBackground(bucket) {
    if (bucket && typeof bucket.image_url === 'string' && bucket.image_url) {
      return 'url(' + bucket.image_url + ')';
    }

    if (bucket && typeof bucket.color_code === 'string' && SEARCHANISE_HEX_PATTERN.test(bucket.color_code)) {
      var hex = '#' + bucket.color_code;
      return 'linear-gradient(to right, ' + hex + ', ' + hex + ')';
    }

    var swatches = (window.themeVariables.settings && window.themeVariables.settings.facetSwatches) || {};
    var swatch = swatches[String(bucket && bucket.value).toLowerCase().trim()];

    if (!swatch) return null;
    if (String(swatch).indexOf('linear-gradient(') === 0) return swatch;
    if (String(swatch).indexOf('#') === 0) return 'linear-gradient(to right, ' + swatch + ', ' + swatch + ')';

    return 'url(' + swatch + ')';
  }

  // White needs a visible outline or it disappears against a light panel —
  // the theme decides this by comparing the label with the translated "white"
  // (general.label.white can hold several comma-separated spellings).
  function isWhiteSwatch(bucket) {
    var labels = (window.themeVariables.settings && window.themeVariables.settings.whiteLabels) || '';
    var value = String(bucket && bucket.value).toLowerCase().trim();
    var named = labels
      .split(',')
      .map(function (entry) {
        return entry.toLowerCase().trim();
      })
      .indexOf(value) !== -1;

    return named || String(bucket && bucket.color_code).toUpperCase() === 'FFFFFF';
  }

  // KAN-133 (1c/1g): the icon or colour chip shown next to a filter value,
  // from whichever source has one.
  //
  // A filter whose Appearance is set to "Color values as color swatches" in
  // the Searchanise dashboard carries its own artwork on every value —
  // `color_code` from the assigned Color family, or `image_url`. That is the
  // merchant's own configuration, so it wins.
  //
  // Everything else (Gems, Bead Shape, Birthstones …) is not a colour filter
  // and never carries either, so those fall back to the theme's
  // `color_swatch_config` — the same list facets.liquid resolves for the
  // native filters, which is what keeps the gemstone icons identical in both
  // places. A value in neither renders as plain text, as it does natively.
  function swatchHtml(bucket) {
    var value = bucket && bucket.value;

    if (bucket && typeof bucket.image_url === 'string' && bucket.image_url) {
      return '<img src="' + escapeHtml(bucket.image_url) + '" alt="" class="filter-swatch-image" width="24" height="24" loading="lazy">';
    }

    if (bucket && typeof bucket.color_code === 'string' && SEARCHANISE_HEX_PATTERN.test(bucket.color_code)) {
      return '<span class="filter-swatch-color" style="background: #' + escapeHtml(bucket.color_code) + '"></span>';
    }

    var swatches = (window.themeVariables.settings && window.themeVariables.settings.facetSwatches) || {};
    var swatch = swatches[String(value).toLowerCase().trim()];

    if (!swatch) return '';

    if (String(swatch).indexOf('#') === 0 || String(swatch).indexOf('gradient') !== -1) {
      return '<span class="filter-swatch-color" style="background: ' + escapeHtml(swatch) + '"></span>';
    }

    return '<img src="' + escapeHtml(swatch) + '" alt="" class="filter-swatch-image" width="24" height="24" loading="lazy">';
  }

  var CHEVRON_ICON =
    '<svg aria-hidden="true" focusable="false" fill="none" width="10" class="group-expanded:rotate icon icon-chevron-down" viewBox="0 0 10 10">' +
    '<path d="m1 3 4 4 4-4" stroke="currentColor" stroke-linecap="square"/></svg>';

  var CLOSE_ICON =
    '<svg aria-hidden="true" focusable="false" fill="none" width="10" class="icon icon-close" viewBox="0 0 16 16">' +
    '<path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1.5"/></svg>';

  // Same markup snippets/icon.liquid emits for pagination's arrows, including
  // the direction-aware class that flips them in RTL.
  var CHEVRON_LEFT_ICON =
    '<svg aria-hidden="true" focusable="false" fill="none" width="11" class="icon icon-chevron-left icon--direction-aware" viewBox="0 0 10 10">' +
    '<path d="M7 1 3 5l4 4" stroke="currentColor" stroke-linecap="square"/></svg>';

  var CHEVRON_RIGHT_ICON =
    '<svg aria-hidden="true" focusable="false" fill="none" width="11" class="icon icon-chevron-right icon--direction-aware" viewBox="0 0 10 10">' +
    '<path d="m3 9 4-4-4-4" stroke="currentColor" stroke-linecap="square"/></svg>';

  // The page numbers to show, with '…' where a run is elided: always the
  // first and last page plus a window around the current one. Mirrors what
  // Shopify's `paginate.parts` produces, so a paginated Searchanise result
  // set and a native one look the same.
  function buildPaginationParts(current, pages) {
    var parts = [];
    var last = 0;

    for (var page = 1; page <= pages; page++) {
      var keep = page === 1 || page === pages || Math.abs(page - current) <= 1;
      if (!keep) continue;

      if (last && page - last > 1) parts.push('…');
      parts.push(page);
      last = page;
    }

    return parts;
  }

  // A real href on every page link, so the pagination is crawlable and
  // middle-clickable even though the click itself is handled in JS.
  function pageUrl(page) {
    var url = new URL(window.location.href);
    var params = new URLSearchParams(url.search);

    if (page > 1) {
      params.set('page', page);
    } else {
      params.delete('page');
    }

    url.search = params.toString();

    return url.toString();
  }

  // Theme strings come through the `t` filter, so they arrive HTML-escaped.
  // That is what we want when they land in innerHTML, but not for attribute
  // values set from JS (aria-label), hence this.
  function decodeEntities(value) {
    var textarea = document.createElement('textarea');
    textarea.innerHTML = String(value == null ? '' : value);
    return textarea.value;
  }

  function slugify(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Built-in facets already have readable attribute names ("price",
  // "product_type", "collections", …) so those double as the URL key.
  // Metafield and product-option facets do not — theirs is an opaque hash
  // ("filter_metafield_686a137e…", "filter_option_af804f7e…"), which would
  // make the hand-written "/search?q=emerald&filter.…=Emerald" URLs from the
  // ticket unusable, so those are keyed on a slug of the facet's title.
  var SEARCHANISE_OPAQUE_ATTRIBUTE_PREFIXES = ['filter_metafield_', 'filter_option_'];

  function facetSlug(facet) {
    var attribute = String(facet.attribute);
    var opaque = SEARCHANISE_OPAQUE_ATTRIBUTE_PREFIXES.some(function (prefix) {
      return attribute.indexOf(prefix) === 0;
    });

    if (!opaque) return attribute;

    return slugify(facet.title) || attribute;
  }

  function readSlugMap() {
    try {
      return JSON.parse(window.localStorage.getItem(SEARCHANISE_SLUG_STORAGE_KEY)) || {};
    } catch (err) {
      return {};
    }
  }

  function writeSlugMap(map) {
    try {
      window.localStorage.setItem(SEARCHANISE_SLUG_STORAGE_KEY, JSON.stringify(map));
    } catch (err) {
      /* private mode / quota — the map is only ever a cache */
    }
  }

  function readStateFromUrl() {
    var params = new URLSearchParams(window.location.search);
    // `page` is the same parameter name Shopify's own pagination uses, so a
    // shared /search URL reads identically whether it was produced here or by
    // the native branch of main-search.liquid.
    var page = parseInt(params.get('page'), 10);
    var state = { sort: params.get('sort_by') || '', page: page > 0 ? page : 1, filters: {} };

    params.forEach(function (value, key) {
      if (key.indexOf('filter.') !== 0 || value === '') return;
      var slug = key.slice('filter.'.length);
      (state.filters[slug] = state.filters[slug] || []).push(value);
    });

    return state;
  }

  function stateToSearchParams(state) {
    var params = new URLSearchParams();

    // Everything the theme did not put there (q, and any campaign/tracking
    // parameters) is preserved; sort_by, page and filter.* are owned by this
    // state.
    new URLSearchParams(window.location.search).forEach(function (value, key) {
      if (key.indexOf('filter.') === 0 || key === 'sort_by' || key === 'page') return;
      params.append(key, value);
    });

    Object.keys(state.filters).forEach(function (slug) {
      state.filters[slug].forEach(function (value) {
        params.append('filter.' + slug, value);
      });
    });

    if (state.sort) params.set('sort_by', state.sort);
    if (state.page > 1) params.set('page', state.page);

    return params;
  }

  function countActiveValues(state) {
    return Object.keys(state.filters).reduce(function (total, slug) {
      return total + state.filters[slug].length;
    }, 0);
  }

  // === <searchanise-search-results> — /search results page ===
  // Renders into the server-rendered shell from sections/main-search.liquid:
  // the product grid, the facet sidebar/drawer, the sort popover state, the
  // active-filter chips and the Collections/Pages/Blog tabs.
  window.customElements.define('searchanise-search-results', class extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-searchanise-grid]');
      this.emptyState = this.querySelector('[data-searchanise-empty]');
      this.paginationWrapper = this.querySelector('[data-searchanise-pagination]');
      this.toolbar = this.querySelector('[data-searchanise-toolbar]');
      this.filterButton = this.querySelector('[data-searchanise-filter-button]');
      this.sortPopover = this.querySelector('[data-searchanise-sort-popover]');
      this.activeFacets = this.querySelector('[data-searchanise-active-facets]');
      this.collectionGrid = this.querySelector('[data-searchanise-collection]');
      this.facetContainers = Array.prototype.slice.call(this.querySelectorAll('[data-searchanise-facets]'));

      this.query = this.getAttribute('data-query') || '';
      this.perPage = parseInt(this.getAttribute('data-per-page'), 10) || 24;
      this.showFilters = this.getAttribute('data-show-filters') === 'true';
      this.showValuesCount = this.getAttribute('data-show-values-count') === 'true';
      this.showGroupName = this.getAttribute('data-show-group-name') === 'true';
      this.openFiltersByDefault = this.getAttribute('data-open-filters') === 'true';

      this.state = readStateFromUrl();
      this.slugMap = readSlugMap();
      this.facets = [];
      this.total = 0;
      // Resolved from the first response's data rather than assumed, so a
      // currency the catalogue has no market prices for degrades to the base
      // price field instead of filtering on a field that does not exist.
      this.priceField = 'price';
      this.priceRatio = 1;
      this.priceResolved = false;
      this.slugRetryDone = false;
      this.pageRetryDone = false;
      this.syncingSort = false;
      // What is typed into each filter group's own search box, keyed the same
      // way as the open/closed state so both survive a re-render.
      this.facetSearchTerms = {};

      // Whatever Liquid marked as selected is the store's default sort, and it
      // is what a URL with no sort_by means — needed to put the listbox back
      // after the shopper navigates back to such a URL.
      var selectedOption = this.sortPopover && this.sortPopover.querySelector('[role="option"][aria-selected="true"]');
      this.defaultSort = selectedOption ? selectedOption.value : '';

      this.listeners = new AbortController();
      var signal = this.listeners.signal;

      // Deliberately not delegated from the host: <facets-drawer> extends the
      // theme's Modal, which moves itself to <body> while open (theme.js,
      // shouldAppendToBody), so a listener up here would stop seeing anything
      // the shopper does in the mobile filter drawer. The containers travel
      // with it, and survive their own innerHTML being replaced.
      this.facetContainers.forEach(function (container) {
        container.addEventListener('change', this.onFacetChange.bind(this), { signal: signal });
        container.addEventListener('input', this.onFacetSearch.bind(this), { signal: signal });
      }, this);

      // Chips, "Clear all" and the pagination live in .collection__main, which
      // never moves, so those stay delegated.
      this.addEventListener('click', this.onClick.bind(this), { signal: signal });
      if (this.sortPopover) {
        this.sortPopover.addEventListener('listbox:change', this.onSortChange.bind(this), { signal: signal });
      }
      window.addEventListener('popstate', this.onPopState.bind(this), { signal: signal });

      if (this.query === '') {
        this.showEmpty();
        return;
      }

      this.fetchResults(true);
    }

    disconnectedCallback() {
      if (this.listeners) this.listeners.abort();
    }

    // ---------------------------------------------------------------- data

    // Maps the URL's filter slugs back onto Searchanise attributes. Built-in
    // facets resolve without any lookup (slug === attribute); metafield ones
    // need the slug map, which is only known once a response with facets has
    // come back — see the retry in renderPage().
    resolveFilters() {
      var resolved = {};

      Object.keys(this.state.filters).forEach(function (slug) {
        var attribute = this.slugMap[slug] || slug;
        resolved[attribute] = this.state.filters[slug];
      }, this);

      return resolved;
    }

    buildParams(refreshFacets) {
      var params = {
        q: this.query,
        items: true,
        maxResults: this.perPage,
        startIndex: (this.state.page - 1) * this.perPage
      };

      // Facets describe the whole filtered result set, not the page being
      // shown, so turning a page does not re-request them. Collections and
      // pages are not requested at all any more — KAN-133 (2b) moved them to
      // the header dropdown.
      if (refreshFacets) params.facets = this.showFilters;

      var sort = SEARCHANISE_SORT_MAP[this.state.sort];
      if (sort) {
        params.sortBy = sort.sortBy;
        if (sort.sortOrder) params.sortOrder = sort.sortOrder;
      }

      var filters = this.resolveFilters();
      Object.keys(filters).forEach(function (attribute) {
        var key = attribute === 'price' ? this.priceField : attribute;
        // Several values of one facet are an OR ("|"); the price slider is a
        // single "min,max" range, which is why it never goes through here
        // with more than one entry.
        params['restrictBy[' + key + ']'] = filters[attribute].join('|');
      }, this);

      return params;
    }

    // `refreshFacets` marks a request whose filter or sort changed — the facet
    // list has to be rebuilt from it. Turning a page passes false: the grid is
    // still replaced (never appended, KAN-133 item 3), but the sidebar, chips
    // and toolbar already describe this result set and are left alone.
    fetchResults(refreshFacets) {
      if (this.grid) this.grid.setAttribute('aria-busy', 'true');

      if (this.fetchController) this.fetchController.abort();
      this.fetchController = new AbortController();
      var signal = this.fetchController.signal;

      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      return searchaniseFetch('getresults', this.buildParams(refreshFacets))
        .then(function (data) {
          if (signal.aborted) return;

          return attachLivePrices((data && data.items) || [], function (late) {
            if (!signal.aborted) liveRenderedPrices(this.grid, late);
          }.bind(this)).then(function () {
            if (signal.aborted) return;
            this.renderPage(data, refreshFacets);
          }.bind(this));
        }.bind(this))
        .catch(function (err) {
          if (signal.aborted) return;
          if (window.console && console.warn) console.warn('[searchanise]', err);

          // A page change that fails keeps the results already on screen —
          // the shopper still has a usable page, and the pagination they just
          // clicked is still there to try again. Only a request that was
          // going to replace the result set anyway (new query, filter or
          // sort) falls back to the empty state, because what is on screen no
          // longer answers the question being asked.
          if (refreshFacets) {
            this.showEmpty(countActiveValues(this.state) > 0);
          } else if (this.grid) {
            this.grid.removeAttribute('aria-busy');
          }
        }.bind(this))
        .finally(function () {
          // A superseded request stays silent: <loading-bar> runs the bar to
          // completion on every "end" rather than counting them, so ending
          // here would blank the bar while the request that replaced this one
          // is still in flight. That one dispatches its own "end".
          if (signal.aborted) return;
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        }.bind(this));
    }

    // The price facet's bounds come back in the shop's base currency, but
    // restrictBy works on the per-market price field too (verified against
    // live data on KAN-133), so the slider can talk to the shopper in their
    // own currency: filter on the market field, and scale the base bounds by
    // the market/base ratio the returned items themselves imply. Across the
    // whole catalogue that ratio varies by well under 1% — invisible once the
    // bounds are rounded outwards, and the values sent back to the API are
    // exact regardless, since they are whatever the shopper typed.
    resolveMarketPricing(items) {
      if (this.priceResolved) return;

      var settings = window.themeVariables.settings;
      var country = settings.activeCountryCode;
      var currency = activeCurrency();
      var ratios = [];
      var field = null;

      items.forEach(function (item) {
        var variant = (item.shopify_variants || [])[0];
        var base = parseFloat(item.price);
        if (!variant || !base) return;

        if (!field) {
          var exact = 'price_' + country + '_' + currency;
          if (variant[exact] != null && parseFloat(variant[exact]) > 0) {
            field = exact;
          } else {
            var suffix = '_' + currency;
            for (var key in variant) {
              if (key.indexOf('price_') === 0 && key.slice(-suffix.length) === suffix && parseFloat(variant[key]) > 0) {
                field = key;
                break;
              }
            }
          }
        }

        if (field && variant[field]) {
          var market = parseFloat(variant[field]);
          if (market > 0) ratios.push(market / base);
        }
      });

      if (field && ratios.length) {
        ratios.sort(function (a, b) {
          return a - b;
        });
        this.priceField = field;
        this.priceRatio = ratios[Math.floor(ratios.length / 2)];
      }

      // Only settled once there was something to read it from. Latching on an
      // empty result set — a shared URL whose filters match nothing, say —
      // would freeze the base-currency field in place for the rest of the
      // session, so the price slider would then show euro bounds while
      // filtering on USD amounts.
      if (items.length) this.priceResolved = true;
    }

    renderPage(data, refreshFacets) {
      // KAN-133 (1a): sold-out products last, within the page being rendered.
      var items = sortAvailableFirst((data && data.items) || []);
      this.total = (data && data.totalItems) || 0;

      if (refreshFacets) {
        this.resolveMarketPricing(items);

        if (data && data.facets) {
          this.facets = data.facets.filter(function (facet) {
            return SEARCHANISE_HIDDEN_FACETS.indexOf(facet.attribute) === -1 && (facet.buckets || []).length > 0;
          });

          // First response is also the first chance to translate a metafield
          // slug from a shared URL into its attribute. If that changes what
          // should have been filtered, re-run once with the real attributes.
          if (this.rebuildSlugMap() && !this.slugRetryDone) {
            this.slugRetryDone = true;
            this.fetchResults(true);
            return;
          }
        }
      }

      // A page past the end of the result set — a bookmarked or crawled URL
      // whose ?page= outlived the results it was written for — would
      // otherwise read as "no results" for a query that has plenty. The API
      // gives nothing to distinguish the two cases (an out-of-range
      // startIndex answers with totalItems: 0, not with the real total), so
      // the only way to tell is to ask for the first page: if that is empty
      // too, the search really did match nothing, and the empty state below
      // is correct. Replaces rather than pushes history, since the
      // out-of-range URL is not somewhere "back" should return to.
      if (items.length === 0 && this.state.page > 1 && !this.pageRetryDone) {
        this.pageRetryDone = true;
        this.state.page = 1;
        this.replaceState();
        this.fetchResults(refreshFacets);
        return;
      }

      if (items.length === 0) {
        // With filters on, an empty result set is a dead end the shopper can
        // back out of, so the facets and chips stay on screen. A search that
        // matched nothing at all is different: the API still returns a price
        // facet covering an empty range, and a sidebar and sort control over
        // no results are just furniture, so they go away entirely.
        var filtered = countActiveValues(this.state) > 0;
        var emptyFocusId = this.captureFocusId();

        this.showEmpty(filtered);

        if (filtered) {
          this.renderFacets();
          this.renderActiveFacets();
          this.renderToolbar();
          this.restoreFocus(emptyFocusId);
        }

        return;
      }

      this.pageRetryDone = false;

      if (this.grid) {
        this.grid.removeAttribute('aria-busy');
        this.grid.hidden = false;
        // Always replaced, never appended: pages are now discrete (item 3).
        this.grid.innerHTML = '';
        items.forEach(function (item) {
          this.grid.appendChild(buildProductCard(item));
        }, this);
      }

      if (this.emptyState) this.emptyState.hidden = true;
      this.renderCount();

      if (refreshFacets) {
        // Captured across the whole re-render, not just the facet list: the
        // control the shopper acted on may have been a chip, which lives in a
        // different container that is also about to be replaced.
        var focusId = this.captureFocusId();

        this.renderFacets();
        this.renderActiveFacets();
        this.renderToolbar();
        this.restoreFocus(focusId);
      }

      this.renderPagination();
    }

    // ---------------------------------------------------------- pagination

    // KAN-133 (item 3): numbered pages rather than "Load more", mirroring
    // snippets/pagination.liquid — same markup and classes, so it inherits
    // the theme's pagination styling, and the same "…" gap rule Shopify
    // applies (first, last, and a window around the current page).
    pageCount() {
      return Math.max(1, Math.ceil(this.total / this.perPage));
    }

    renderPagination() {
      if (!this.paginationWrapper) return;

      var pages = this.pageCount();
      var current = Math.min(this.state.page, pages);

      if (pages <= 1) {
        this.paginationWrapper.innerHTML = '';
        this.paginationWrapper.hidden = true;
        return;
      }

      var strings = window.themeVariables.strings;
      var html =
        '<nav class="pagination" role="navigation" aria-label="' + escapeHtml(decodeEntities(strings.paginationLabel)) + '">';

      var goToLabel = function (index) {
        return escapeHtml(decodeEntities(strings.paginationGoToPage).replace('__INDEX__', index));
      };

      var link = function (page, content, rel) {
        return (
          '<a class="pagination__link h6" href="' + escapeHtml(pageUrl(page)) + '"' +
          (rel ? ' rel="' + rel + '"' : '') +
          ' aria-label="' + goToLabel(page) + '" data-searchanise-page="' + page + '">' +
          content +
          '</a>'
        );
      };

      if (current > 1) html += link(current - 1, CHEVRON_LEFT_ICON, 'prev');

      buildPaginationParts(current, pages).forEach(function (part) {
        if (part === '…') {
          html += '<span class="pagination__link pagination__link--disabled h6">&hellip;</span>';
          return;
        }

        if (part === current) {
          html += '<span class="pagination__link pagination__link--disabled h6" aria-current="page">' + part + '</span>';
          return;
        }

        html += link(part, String(part));
      });

      if (current < pages) html += link(current + 1, CHEVRON_RIGHT_ICON, 'next');

      this.paginationWrapper.innerHTML = html + '</nav>';
      this.paginationWrapper.hidden = false;
    }

    goToPage(page) {
      var pages = this.pageCount();
      var target = Math.min(Math.max(1, page), pages);
      if (target === this.state.page) return;

      this.state.page = target;
      this.pushState();
      this.fetchResults(false);

      // Page 2 rendered at the scroll position of page 1's footer looks like
      // nothing happened. The native paginated collection reloads the
      // document and lands at the top; this is the client-side equivalent,
      // aligned on the results rather than the page header.
      if (this.collectionGrid) {
        this.collectionGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    rebuildSlugMap() {
      var before = JSON.stringify(this.resolveFilters());

      this.facets.forEach(function (facet) {
        this.slugMap[facetSlug(facet)] = facet.attribute;
      }, this);

      writeSlugMap(this.slugMap);

      return JSON.stringify(this.resolveFilters()) !== before;
    }

    // ------------------------------------------------------------ rendering

    renderCount() {
      // The count sits in the section header, above (not inside) this element.
      var countEl = document.querySelector('[data-searchanise-count]');
      if (!countEl) return;

      var strings = window.themeVariables.strings;
      var template = this.total === 1 ? strings.searchResultsCountOne : strings.searchResultsCountOther;

      // Replacer functions, not strings: String.replace treats "$&" and
      // friends in a string replacement as backreferences, so a query like
      // "a$&b" would splice the placeholder back into its own output.
      countEl.innerHTML = template
        .replace('__COUNT__', function () {
          return escapeHtml(this.total);
        }.bind(this))
        .replace('__TERMS__', function () {
          return escapeHtml(this.query);
        }.bind(this));
    }

    renderToolbar() {
      if (!this.toolbar) return;

      var strings = window.themeVariables.strings;
      var activeCount = countActiveValues(this.state);
      var hasFacets = this.showFilters && this.facets.length > 0;

      if (this.filterButton) {
        this.filterButton.parentElement.hidden = !hasFacets;
        this.filterButton.innerHTML = strings.facetsFilterButton + (activeCount > 0 ? ' (' + activeCount + ')' : '');
      }

      if (this.collectionGrid) {
        if (hasFacets) {
          this.collectionGrid.setAttribute('data-searchanise-has-facets', '');
        } else {
          this.collectionGrid.removeAttribute('data-searchanise-has-facets');
        }
      }

      this.toolbar.hidden = !hasFacets && !this.sortPopover;

      this.syncSortPopover();
    }

    // <x-listbox> keys everything off aria-activedescendant, not aria-selected:
    // it derives aria-selected from it, and only emits listbox:change when the
    // attribute's value actually changes. Setting aria-selected alone would
    // leave the listbox pointing at the previous option after a back
    // navigation, so re-picking that option would be a no-op change and never
    // reach onSortChange. Writing the attribute re-enters our own handler
    // synchronously, hence the guard.
    syncSortPopover() {
      if (!this.sortPopover) return;

      var listbox = this.sortPopover.querySelector('x-listbox');
      var wanted = this.state.sort || this.defaultSort;
      if (!listbox || !wanted) return;

      var options = Array.prototype.slice.call(this.sortPopover.querySelectorAll('[role="option"]'));
      var match = options.filter(function (option) {
        return option.value === wanted;
      })[0];

      if (!match || !match.id || listbox.getAttribute('aria-activedescendant') === match.id) return;

      this.syncingSort = true;
      listbox.setAttribute('aria-activedescendant', match.id);
      this.syncingSort = false;
    }

    // The open drawer is not a descendant of this element (theme.js moves it
    // to <body>), so anything that walks the rendered UI has to look in the
    // facet containers as well as in here.
    focusRoots() {
      return [this].concat(this.facetContainers);
    }

    captureFocusId() {
      var active = document.activeElement;
      if (!active || !active.id) return null;

      var inside = this.focusRoots().some(function (root) {
        return root.contains(active);
      });

      return inside ? active.id : null;
    }

    restoreFocus(id) {
      if (!id || !window.CSS || !window.CSS.escape) return;

      var selector = '#' + window.CSS.escape(id);
      var roots = this.focusRoots();

      for (var i = 0; i < roots.length; i++) {
        var element = roots[i].querySelector(selector);
        if (element) {
          element.focus();
          return;
        }
      }

      // Removing a chip destroys the very control that was focused. Handing
      // focus to whatever chip took its place keeps keyboard users in the
      // filter list instead of dropping them back to <body>.
      if (id.indexOf('searchanise-chip-') === 0 && this.activeFacets) {
        var next = this.activeFacets.querySelector('[data-facet-remove], [data-facet-clear-all]');
        if (next) next.focus();
      }
    }

    renderFacets() {
      if (!this.showFilters) return;

      // Every filter change re-renders the list from the new counts, which
      // would otherwise snap open accordions shut. Which groups were open is
      // captured first so the panel behaves like it was updated in place
      // rather than replaced.
      var reopen = {};
      this.facetContainers.forEach(function (container) {
        container.querySelectorAll('[data-facet-group] > details[open]').forEach(function (details) {
          reopen[details.parentElement.getAttribute('data-facet-group')] = true;
        });
      });

      this.facetContainers.forEach(function (container) {
        var context = container.getAttribute('data-searchanise-facets');
        var sidebar = container.closest('.facets-sidebar');

        container.innerHTML =
          '<div class="accordion-list">' +
          this.facets
            .map(function (facet) {
              return this.facetHtml(facet, context, reopen);
            }, this)
            .join('') +
          '</div>';

        // Whatever was typed into a group's search box is put back and
        // re-applied, so ticking a value does not silently undo the shopper's
        // narrowing of the list they ticked it in.
        container.querySelectorAll('[data-facet-group]').forEach(function (group) {
          var term = this.facetSearchTerms[group.getAttribute('data-facet-group')];
          var input = term ? group.querySelector('[data-facet-value-search]') : null;
          if (!input) return;

          input.value = term;
          applyFacetSearch(group, term.trim().toLowerCase());
        }, this);

        if (sidebar) sidebar.hidden = this.facets.length === 0;
      }, this);
    }

    facetHtml(facet, context, reopen) {
      if (context === 'drawer' && facet.show_on_mobile === 'N') return '';

      var groupKey = context + ':' + facetSlug(facet);
      // KAN-133 (1e): in the mobile drawer every group starts collapsed, so
      // the shopper sees the list of filters rather than one long scroll
      // through the first group's values. Deliberately not taken from the
      // dashboard's `mobile_view` or from the theme's "open filters by
      // default" setting — both describe the desktop sidebar, where the
      // groups sit side by side with the results and opening them costs
      // nothing. Reopening one by hand still survives a re-render.
      var isOpen =
        reopen[groupKey] ||
        (context === 'drawer' ? false : this.openFiltersByDefault || facet.desktop_view === 'expanded');
      var body = facet.type === 'slider' ? this.priceFacetHtml(facet, context) : this.listFacetHtml(facet, context);
      if (!body) return '';

      return (
        '<accordion-disclosure class="accordion" data-facet-group="' + escapeHtml(groupKey) + '">' +
        '<details class="accordion__disclosure group"' +
        (isOpen ? ' open aria-expanded="true"' : ' aria-expanded="false"') +
        '>' +
        '<summary><span class="accordion__toggle h6">' +
        '<span class="text-with-icon gap-4">' +
        escapeHtml(facet.title) +
        '</span>' +
        CHEVRON_ICON +
        '</span></summary>' +
        '<div class="accordion__content">' +
        body +
        '</div>' +
        '</details>' +
        '</accordion-disclosure>'
      );
    }

    listFacetHtml(facet, context) {
      var slug = facetSlug(facet);
      var active = this.state.filters[slug] || [];
      var buckets = facet.buckets || [];

      // A filter the merchant set to "Color values as color swatches" renders
      // as a wrapping grid of tiles with no labels — the same shape the theme
      // gives a native colour filter (facets.liquid switches to `h-stack wrap`
      // and the option-value snippet's `swatch` type for exactly this case).
      // Everything else stays a labelled checkbox list.
      var asSwatches = facet.appearance === 'color';
      var settings = window.themeVariables.settings;

      var options = buckets
        .map(function (bucket, index) {
          var value = String(bucket.value);
          // Searchanise reports which values are selected, but only for the
          // request it just answered — the URL is what survives a reload, so
          // that is what drives the checked state.
          var checked = active.indexOf(value) !== -1;
          var label = this.showValuesCount && bucket.count != null ? value + ' (' + bucket.count + ')' : value;
          var id = 'searchanise-facet-' + context + '-' + slug + '-' + index;
          // The value is carried on the row so the in-filter search can match
          // it without re-reading the label, which may have a count appended.
          var row = ' data-facet-option="' + escapeHtml(value.toLowerCase()) + '"';
          var input =
            '<input id="' + escapeHtml(id) + '" type="checkbox"' +
            ' data-facet-slug="' + escapeHtml(slug) + '"' +
            ' value="' + escapeHtml(value) + '"' +
            (checked ? ' checked' : '');

          if (asSwatches) {
            var background = swatchBackground(bucket);

            return (
              '<div class="contents"' + row + '>' +
              input + ' class="sr-only">' +
              '<label class="color-swatch' +
              (settings.roundColorSwatches ? ' rounded-full' : '') +
              (isWhiteSwatch(bucket) ? ' border' : '') +
              // The tooltip and the screen-reader name stay the bare colour,
              // without the result count: a tile has no room to explain a
              // number, and the native swatch filter does not show one either.
              '" for="' + escapeHtml(id) + '" data-tooltip="' + escapeHtml(value) + '"' +
              (background ? ' style="--swatch-background: ' + escapeHtml(background) + '"' : '') +
              '>' +
              '<span class="sr-only">' + escapeHtml(value) + '</span>' +
              '</label>' +
              '</div>'
            );
          }

          return (
            '<div class="checkbox-control"' + row + '>' +
            input + ' class="dot-checkbox">' +
            '<label for="' + escapeHtml(id) + '">' + swatchHtml(bucket) + escapeHtml(label) + '</label>' +
            '</div>'
          );
        }, this)
        .join('');

      if (!options) return '';

      // KAN-133 (1d): long value lists (Gems is the one the ticket names) get
      // a search box. Searchanise's own "show a search bar in a filter"
      // setting only reaches its widget, so this is built here instead —
      // purely client-side over the values already on screen, so it costs no
      // request and cannot disagree with the counts next to them.
      var search = '';
      if (buckets.length >= SEARCHANISE_FACET_SEARCH_MIN) {
        var strings = window.themeVariables.strings;
        var placeholder = decodeEntities(strings.facetsSearchFilterValues).replace('__NAME__', decodeEntities(facet.title));

        // .input-prefix is the theme's own bordered field wrapper (it is what
        // the price inputs sit in), so the box, its radius and its focus ring
        // come from the theme rather than from a one-off style here.
        search =
          '<label class="filter-value-search input-prefix text-sm">' +
          '<input type="search" class="field" autocomplete="off" spellcheck="false"' +
          ' data-facet-value-search placeholder="' + escapeHtml(placeholder) + '" aria-label="' + escapeHtml(placeholder) + '">' +
          '</label>';
      }

      return (
        search +
        '<div class="' + (asSwatches ? 'h-stack wrap gap-2' : 'v-stack gap-2.5') + '" data-facet-values>' +
        options +
        '</div>' +
        (search
          ? '<p class="text-subdued text-sm" data-facet-search-empty hidden>' +
            escapeHtml(decodeEntities(window.themeVariables.strings.facetsNoFilterValues)) +
            '</p>'
          : '')
      );
    }

    priceFacetHtml(facet, context) {
      var bucket = (facet.buckets || [])[0];
      if (!bucket) return '';

      var settings = window.themeVariables.settings;
      var strings = window.themeVariables.strings;
      var slug = facetSlug(facet);
      var rangeMax = Math.ceil((parseFloat(bucket.to) || 0) * this.priceRatio);
      if (!rangeMax) return '';

      var active = (this.state.filters[slug] || [])[0];
      var bounds = active ? active.split(',') : [];
      var min = Math.max(0, Math.floor(parseFloat(bounds[0]) || 0));
      var max = Math.min(rangeMax, Math.ceil(parseFloat(bounds[1]) || rangeMax));

      var prefix =
        escapeHtml(settings.currencySymbol || '') +
        (settings.currencyCodeEnabled ? escapeHtml(activeCurrency() || '') : '');
      var fromLabel = decodeEntities(strings.facetsPriceFilterFrom);
      var toLabel = decodeEntities(strings.facetsPriceFilterTo);
      var idPrefix = 'searchanise-price-' + context;

      // The two number inputs keep the theme's own names: <price-range> in
      // theme.js looks them up by "filter.v.price.gte"/"lte" to wire the
      // sliders to them. They are never submitted anywhere.
      return (
        '<price-range class="price-range" data-facet-slug="' + escapeHtml(slug) + '" data-range-max="' + rangeMax + '">' +
        '<div class="range-group" style="--range-min: ' +
        (min / rangeMax) * 100 +
        '%; --range-max: ' +
        (max / rangeMax) * 100 +
        '%">' +
        '<input type="range" aria-label="' + escapeHtml(fromLabel) + '" class="range" min="0" max="' + rangeMax + '" value="' + min + '">' +
        '<input type="range" aria-label="' + escapeHtml(toLabel) + '" class="range" min="0" max="' + rangeMax + '" value="' + max + '">' +
        '</div>' +
        '<div class="input-group">' +
        '<label class="input-prefix text-sm">' +
        '<span class="prefix text-subdued">' + prefix + '</span>' +
        '<input aria-label="' + escapeHtml(fromLabel) + '" class="field" type="number" inputmode="numeric"' +
        ' name="filter.v.price.gte" id="' + idPrefix + '-gte" min="0" max="' + rangeMax + '" placeholder="0" autocomplete="off"' +
        (bounds.length ? ' value="' + min + '"' : '') +
        '>' +
        '</label>' +
        '<span class="text-subdued text-sm">' + strings.facetsPriceRangeTo + '</span>' +
        '<label class="input-prefix text-sm">' +
        '<span class="prefix text-subdued">' + prefix + '</span>' +
        '<input aria-label="' + escapeHtml(toLabel) + '" class="field" type="number" inputmode="numeric"' +
        ' name="filter.v.price.lte" id="' + idPrefix + '-lte" min="0" max="' + rangeMax + '" placeholder="' + rangeMax + '" autocomplete="off"' +
        (bounds.length ? ' value="' + max + '"' : '') +
        '>' +
        '</label>' +
        '</div>' +
        '</price-range>'
      );
    }

    renderActiveFacets() {
      if (!this.activeFacets) return;

      var strings = window.themeVariables.strings;
      var chips = [];

      Object.keys(this.state.filters).forEach(function (slug) {
        var facet = this.facetBySlug(slug);
        var isRange = facet && facet.type === 'slider';

        this.state.filters[slug].forEach(function (value, index) {
          // Price bounds are amounts, and the native chip shows them as money
          // (active-facets.liquid) — the raw "400,600" would be the only
          // unformatted price anywhere on the page.
          var label = isRange
            ? value
                .split(',')
                .map(function (bound) {
                  return formatMoney(bound);
                })
                .join(' – ')
            : value;
          if (this.showGroupName && facet) label = facet.title + ': ' + label;

          // Identified so focus can be put back after the chip list is
          // re-rendered — see restoreFocus().
          var id = 'searchanise-chip-' + slug + '-' + index;

          chips.push(
            '<div class="removable-facet">' +
            '<button type="button" id="' + escapeHtml(id) + '" class="tap-area link-faded" data-facet-remove' +
            ' data-facet-slug="' + escapeHtml(slug) + '" data-facet-value="' + escapeHtml(value) + '"' +
            ' aria-label="' + escapeHtml(decodeEntities(strings.facetsRemoveFilter).replace('__NAME__', function () { return label; })) + '">' +
            CLOSE_ICON +
            '</button>' +
            '<span class="text-subdued">' + escapeHtml(label) + '</span>' +
            '</div>'
          );
        }, this);
      }, this);

      if (chips.length > 1) {
        chips.push(
          '<button type="button" class="facets-clear-all text-subdued" data-facet-clear-all>' +
          '<span class="link">' + strings.facetsClearFilters + '</span>' +
          '</button>'
        );
      }

      // The container is the .active-facets element itself rather than a
      // wrapper around one: .collection__main is a grid with a row gap, and
      // an always-present empty div would open a gap above the product list.
      this.activeFacets.innerHTML = chips.join('');
      this.activeFacets.hidden = chips.length === 0;
    }

    facetBySlug(slug) {
      for (var i = 0; i < this.facets.length; i++) {
        if (facetSlug(this.facets[i]) === slug) return this.facets[i];
      }
      return null;
    }

    // ------------------------------------------------------------- behaviour

    onFacetChange(event) {
      var target = event.target;

      if (target.matches('[data-facet-slug]')) {
        var slug = target.getAttribute('data-facet-slug');
        var values = (this.state.filters[slug] || []).slice();
        var value = target.value;

        if (target.checked) {
          if (values.indexOf(value) === -1) values.push(value);
        } else {
          values = values.filter(function (entry) {
            return entry !== value;
          });
        }

        this.applyFilters(slug, values);
        return;
      }

      var priceRange = target.closest && target.closest('price-range[data-facet-slug]');
      if (priceRange) this.onPriceChange(priceRange);
    }

    // KAN-133 (1d): filters the values of one group down to what was typed.
    // Purely a display filter — nothing is requested, and unchecking is still
    // possible because a value that is currently applied is never hidden.
    onFacetSearch(event) {
      var input = event.target;
      if (!input.matches || !input.matches('[data-facet-value-search]')) return;

      var group = input.closest('[data-facet-group]');
      if (!group) return;

      var term = input.value.trim().toLowerCase();
      this.facetSearchTerms[group.getAttribute('data-facet-group')] = input.value;
      applyFacetSearch(group, term);
    }

    onPriceChange(priceRange) {
      var slug = priceRange.getAttribute('data-facet-slug');
      var rangeMax = parseFloat(priceRange.getAttribute('data-range-max')) || 0;
      var min = parseFloat(priceRange.querySelector('input[name="filter.v.price.gte"]').value);
      var max = parseFloat(priceRange.querySelector('input[name="filter.v.price.lte"]').value);

      if (isNaN(min)) min = 0;
      if (isNaN(max)) max = rangeMax;

      // A range covering everything is not a filter — drop it so the chip and
      // the "(1)" counter do not claim otherwise.
      this.applyFilters(slug, min <= 0 && max >= rangeMax ? [] : [min + ',' + max]);
    }

    onClick(event) {
      var removeButton = event.target.closest('[data-facet-remove]');
      if (removeButton) {
        var slug = removeButton.getAttribute('data-facet-slug');
        var value = removeButton.getAttribute('data-facet-value');
        this.applyFilters(
          slug,
          (this.state.filters[slug] || []).filter(function (entry) {
            return entry !== value;
          })
        );
        return;
      }

      if (event.target.closest('[data-facet-clear-all]')) {
        this.state.filters = {};
        this.commit();
        return;
      }

      // Page links carry a working href, so only a plain left click is taken
      // over — modified clicks keep opening a new tab, as on any link.
      var pageLink = event.target.closest('[data-searchanise-page]');
      if (pageLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
        event.preventDefault();
        this.goToPage(parseInt(pageLink.getAttribute('data-searchanise-page'), 10) || 1);
      }
    }

    onSortChange(event) {
      // syncSortPopover() writes aria-activedescendant to keep the listbox in
      // step with the URL, and <x-listbox> answers that by firing this event.
      // Acting on it would push the state we just read back onto the history.
      if (this.syncingSort) return;

      this.state.sort = event.detail.value;
      this.commit();
    }

    onPopState() {
      this.state = readStateFromUrl();
      this.fetchResults(true);
    }

    applyFilters(slug, values) {
      if (values.length) {
        this.state.filters[slug] = values;
      } else {
        delete this.state.filters[slug];
      }

      this.commit();
    }

    stateUrl() {
      var url = new URL(window.location.href);
      url.search = stateToSearchParams(this.state).toString();
      return url.toString();
    }

    pushState() {
      window.history.pushState({ searchanise: true }, '', this.stateUrl());
    }

    replaceState() {
      window.history.replaceState({ searchanise: true }, '', this.stateUrl());
    }

    commit() {
      // Any change to the filters or the sort invalidates the page number:
      // page 4 of the old result set is very likely past the end of the new
      // one, which would render an empty grid over a non-empty search.
      this.state.page = 1;
      this.pushState();
      this.fetchResults(true);
    }

    showEmpty(keepFacets) {
      if (this.grid) {
        this.grid.removeAttribute('aria-busy');
        this.grid.hidden = true;
        this.grid.innerHTML = '';
      }
      if (this.emptyState) {
        this.emptyState.hidden = false;

        // "No results for <terms>" is wrong when the search did match and the
        // filters are what emptied it — but it has to come back if those
        // filters are then cleared, hence keeping the original around.
        var message = this.emptyState.querySelector('[data-searchanise-empty-message]');
        if (message) {
          if (this.emptyMessage == null) this.emptyMessage = message.textContent;
          message.textContent = keepFacets
            ? decodeEntities(window.themeVariables.strings.facetsNoResults)
            : this.emptyMessage;
        }
      }
      if (this.paginationWrapper) {
        this.paginationWrapper.innerHTML = '';
        this.paginationWrapper.hidden = true;
      }

      if (!keepFacets) {
        if (this.toolbar) this.toolbar.hidden = true;
        if (this.activeFacets) this.activeFacets.hidden = true;
        if (this.collectionGrid) this.collectionGrid.removeAttribute('data-searchanise-has-facets');
        this.querySelectorAll('.facets-sidebar').forEach(function (sidebar) {
          sidebar.hidden = true;
        });
      }

      this.renderCount();
    }
  });
})();
